import { Fragment, useState } from 'react';
import {
  Checkbox,
  DataTable,
  IconButton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip
} from '@carbon/react';
import { Edit, Incomplete, Save, TrashCan } from '@carbon/react/icons';
import _ from 'lodash';

import { useIsReadonly } from 'core/readonly';
import WorkstationEditModal from 'features/outputs/WorkstationEditModal';
import { MEASUREMENT_SYSTEM } from 'models/measurementSystem';
import TableExpandText from 'shared/components/data-table/TableExpandText';
import { formatNumeric } from 'utils/format';
import useLatest from 'utils/hooks/useLatest';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import { convertToCurrentSystem, convertToStandardSystem } from 'utils/units';
import styles from 'theme/features/outputs/components/outputs-spaces-table.module.scss';

const EDITABLE_ROW_TYPE = {
  customOutput: 0,
  unitArea: 1,
  quantity: 2
};

const WARNING_TYPE = {
  customOutputMissing: 0,
  customQuantityMissing: 1,
  customUnitAreaMissing: 2,
  notesMissing: 3,
  unitAreaIsTooSmall: 4
};

const EditableTableRow = ({
  children,
  onSave,
  onDelete,
  initialValues,
  isExport = false,
  editType,
  minArea = null,
  measurementSystem
}) => {
  const [values, setValues] = useState(initialValues);
  const [warningType, setWarningType] = useState(null);

  const isReadonly = useIsReadonly();

  const handleChange = (name, value) =>
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }));

  const handleSave = () => {
    if (editType === EDITABLE_ROW_TYPE.customOutput && (!values.unitArea || !values.quantity)) {
      setWarningType(WARNING_TYPE.customOutputMissing);
    } else if (editType === EDITABLE_ROW_TYPE.quantity && !values.quantity) {
      setWarningType(WARNING_TYPE.customQuantityMissing);
    } else if (editType === EDITABLE_ROW_TYPE.unitArea && !values.unitArea) {
      setWarningType(WARNING_TYPE.customUnitAreaMissing);
    } else if (minArea && convertToStandardSystem(Number(values.unitArea), measurementSystem) < minArea) {
      setWarningType(WARNING_TYPE.unitAreaIsTooSmall);
    } else if (!values.notes) {
      setWarningType(WARNING_TYPE.notesMissing);
    } else {
      onSave(values);
    }
  };

  return (
    <TableRow className={styles['edit-row']}>
      {children({ values, onChange: handleChange })}
      <TableCell className={styles['checkbox-cell']}>
        {warningType === WARNING_TYPE.notesMissing && (
          <InlineNotification
            kind="warning"
            title="This change does not have notes or comments!"
            subtitle="Providing comments for any custom quantities is helpful to your team"
            className={styles['notes-warning']}
            onClose={() => setWarningType(null)}
          />
        )}
        {warningType === WARNING_TYPE.customOutputMissing && (
          <InlineNotification
            kind="warning"
            title="This change is not complete!"
            subtitle="To compute output please provide both area and quantity"
            className={styles['notes-warning']}
            onClose={() => setWarningType(null)}
          />
        )}
        {warningType === WARNING_TYPE.customUnitAreaMissing && (
          <InlineNotification
            kind="warning"
            title="This change is not complete!"
            subtitle="To compute output please provide the area"
            className={styles['notes-warning']}
            onClose={() => setWarningType(null)}
          />
        )}
        {warningType === WARNING_TYPE.customQuantityMissing && (
          <InlineNotification
            kind="warning"
            title="This change is not complete!"
            subtitle="To compute output please provide the quantity"
            className={styles['notes-warning']}
            onClose={() => setWarningType(null)}
          />
        )}
        {warningType === WARNING_TYPE.unitAreaIsTooSmall && !!minArea && (
          <InlineNotification
            kind="warning"
            title="This change is not complete!"
            subtitle={`Please provide a minimum area of ${formatNumeric(minArea)} SF / ${convertToCurrentSystem(
              minArea,
              MEASUREMENT_SYSTEM.metric
            )} SM`}
            className={styles['notes-warning']}
            onClose={() => setWarningType(null)}
          />
        )}
        {!isReadonly && !isExport && (
          <IconButton label="Save" kind="ghost" size="sm" onClick={handleSave}>
            <Save size={20} />
          </IconButton>
        )}
      </TableCell>
      {!isReadonly && !isExport && (
        <TableCell>
          <IconButton label="Delete" kind="ghost" size="sm" onClick={onDelete}>
            <TrashCan size={20} />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
};

const OutputsSpacesTable = ({
  data,
  headers,
  outputs,
  renderEditRow,
  rowEdits,
  onRowEdit,
  workstationEdits,
  onWorkstationsEdit,
  isShowingCustomizations,
  isExport = false,
  measurementSystem,
  project,
  workstations
}) => {
  const isReadonly = useIsReadonly();

  const [editableRows, setEditableRows] = useState(new Set());

  const rowEditsRef = useLatest(rowEdits);

  useUpdateEffect(
    () =>
      setEditableRows(
        isShowingCustomizations
          ? new Set(
              Object.keys(rowEditsRef.current).filter(
                (id) =>
                  !_.isEmpty(_.omit(rowEditsRef.current[id]?.value, 'isExcluded')) &&
                  !rowEditsRef.current[id]?.value?.isExcluded
              )
            )
          : new Set()
      ),
    [isShowingCustomizations]
  );

  const closeRowEdit = (id) => setEditableRows(new Set(_.without([...editableRows], id)));

  const handleEditClick = (id) => {
    if (editableRows.has(id)) {
      closeRowEdit(id);
    } else {
      setEditableRows(new Set([...editableRows, id]));
    }
  };

  return (
    <>
      <DataTable
        size="sm"
        rows={data}
        headers={headers}
        render={({ rows, getTableProps, getHeaderProps, getRowProps }) => (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader
                    {...getHeaderProps({ header })}
                    key={header.key}
                    style={{ width: header.width }}
                    className={[styles.alignHeader, 'alignHeader'].join(' ')}
                  >
                    {header.header}
                  </TableHeader>
                ))}
                <TableHeader style={{ width: '1%' }}>Exclude</TableHeader>
                {!isReadonly && !isExport && <TableHeader style={{ width: '1%' }}>Edit</TableHeader>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const space = data[index];
                const output = outputs[space.id];

                const editsInfo = rowEdits?.[row.id];
                const isSaved = space.isWorkstation
                  ? space.isCic
                    ? workstationEdits.cic.saved ?? true
                    : workstationEdits.other.saved ?? true
                  : editsInfo?.isSaved ?? true;
                const edits = editsInfo?.value ?? {};
                const isExcluded = edits.isExcluded ?? output?.isExcluded ?? false;

                const isEditOpen = editableRows.has(row.id);

                const handleSave = (values) => {
                  onRowEdit(row.id, values);

                  if (!isShowingCustomizations) {
                    handleEditClick(row.id);
                  }
                };

                const handleIsExcludedChange = (newIsExcluded) => {
                  onRowEdit(row.id, {
                    isExcluded: newIsExcluded
                  });

                  if (isEditOpen) {
                    closeRowEdit(row.id);
                  }
                };

                const handleWorkstationsChange = (workstations) => {
                  onWorkstationsEdit((prevWorkstations) => ({
                    ...prevWorkstations,
                    other: { values: workstations, saved: false }
                  }));

                  if (isEditOpen) {
                    closeRowEdit(row.id);
                  }
                };

                const handleCicWorkstationsChange = (workstations) => {
                  onWorkstationsEdit((prevWorkstations) => ({
                    ...prevWorkstations,
                    cic: { values: workstations, saved: false }
                  }));

                  if (isEditOpen) {
                    closeRowEdit(row.id);
                  }
                };

                return (
                  <Fragment key={row.id}>
                    <TableRow {...getRowProps({ row })} className={styles['table-row']}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id} className={styles.alignRowData}>
                          {cell.value}
                        </TableCell>
                      ))}
                      <TableCell className={styles['checkbox-cell']}>
                        <Checkbox
                          id={`exclude-${row.id}`}
                          className={styles.checkbox}
                          hideLabel
                          labelText="Exclude"
                          onChange={(_, { checked }) => handleIsExcludedChange(checked)}
                          checked={isExcluded}
                          disabled={isReadonly || isExport}
                        />
                      </TableCell>
                      {!isReadonly && !isExport && (
                        <TableCell>
                          {!isSaved && (
                            <Tooltip
                              className={styles['incomplete-tooltip']}
                              align="left"
                              label="Row is incomplete, save outputs to see updated calculations"
                            >
                              <button type="button">
                                <Incomplete size={12} />
                              </button>
                            </Tooltip>
                          )}
                          <IconButton
                            label="Edit"
                            kind="ghost"
                            size="sm"
                            onClick={() => handleEditClick(row.id)}
                            disabled={isExcluded}
                          >
                            <Edit size={20} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    {space.isWorkstation && !space.isCic && project && workstations && (
                      <WorkstationEditModal
                        open={isEditOpen}
                        onCancel={() => closeRowEdit(row.id)}
                        onSubmit={handleWorkstationsChange}
                        employeePopulation={project.employeePopulation}
                        workstations={workstations.population}
                        workstationEdits={workstationEdits.other.values}
                        isCic={false}
                      />
                    )}
                    {space.isWorkstation && space.isCic && project && workstations && (
                      <WorkstationEditModal
                        open={isEditOpen}
                        onCancel={() => closeRowEdit(row.id)}
                        onSubmit={handleCicWorkstationsChange}
                        employeePopulation={project.employeePopulation}
                        workstations={workstations}
                        workstationEdits={workstationEdits.cic.values}
                        isCic
                      />
                    )}
                    {isEditOpen && !space.isWorkstation && (
                      <EditableTableRow
                        initialValues={edits}
                        isSaved={isSaved}
                        onEdit={() => handleEditClick(row.id)}
                        onDelete={() => handleSave({})}
                        onSave={handleSave}
                        isExport={isExport}
                        minArea={space?.minEditArea}
                        measurementSystem={measurementSystem}
                        editType={
                          space?.isCustomOutput
                            ? EDITABLE_ROW_TYPE.customOutput
                            : space?.isCustomArea
                            ? EDITABLE_ROW_TYPE.unitArea
                            : EDITABLE_ROW_TYPE.quantity
                        }
                      >
                        {({ values, onChange }) =>
                          renderEditRow({
                            id: row.id,
                            rowEdits: values,
                            onEdit: onChange
                          })
                        }
                      </EditableTableRow>
                    )}
                    {isExport && edits.notes && (
                      <TableRow className={styles['edit-row']}>
                        <TableCell colSpan={row.cells.length - 4} />
                        <TableCell colSpan={5}>
                          <TableExpandText>{edits.notes}</TableExpandText>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      />
    </>
  );
};

export default OutputsSpacesTable;
